import styled, { keyframes, css } from 'styled-components';

////////////////////////////////////////////////////////
// Animations
export const breathing = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

export const ripple = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(74, 144, 226, 0.6);
  }
  70% {
    box-shadow: 0 0 0 25px rgba(74, 144, 226, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(74, 144, 226, 0);
  }
`;

export const pulse = keyframes`
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(74, 144, 226, 0.3);
  }
  50% { 
    transform: scale(1.02);
    box-shadow: 0 0 0 10px rgba(74, 144, 226, 0);
  }
`;

// Styled Components
export const AppContainer = styled.div`
  display: flex;
  height: 100vh;
`;

export const SidePanel = styled.div`
  width: 320px;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  padding: 20px;
  overflow-y: auto;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
`;

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
`;

export const ConfigSection = styled.div`
  margin-bottom: 24px;
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

export const ConfigTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

export const ConfigLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

export const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  margin-bottom: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
  
  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
  
  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const StatusBadge = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: ${props => {
    switch (props.status) {
      case 'speaking': return '#10b981';
      case 'timeout': return '#f59e0b';
      case 'connected': return '#4a90e2';
      default: return '#6b7280';
    }
  }};
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  ${props => props.active && css`
    animation: ${pulse} 1.5s ease-in-out infinite;
  `}
`;

export const ButtonWrapper = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
  margin-bottom: 24px;
`;

export const VoiceButton = styled.button`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid ${props => {
    if (!props.connected) return '#cbd5e1';
    return '#4a90e2';
  }};
  background: ${props => {
    if (!props.connected) return '#ffffff';
    return 'linear-gradient(135deg, #ebf4ff 0%, #dbeafe 100%)';
  }};
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
  }

  ${props =>
    props.connected && props.listening &&
    css`
      animation: ${breathing} 2s ease-in-out infinite, ${ripple} 2.5s infinite;
    `}
`;

export const LottieWrapper = styled.div`
  position: absolute;
  width: 70px;
  height: 70px;
  top: 35px;
  left: 35px;
  pointer-events: none;
`;

export const StatusText = styled.div`
  font-size: 18px;
  color: #475569;
  font-weight: 500;
  text-align: center;
  margin-bottom: 32px;
`;

export const ConnectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.connected ? '#10b981' : '#6b7280'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
`;

///////////////////////////////////////////